/**
 * Shared helper for the "store vs website" derivation used by the
 * readiness page, the Settings → AI Discovery llms.txt preview, and
 * anywhere else the shape of the generated llms.txt or the applicable
 * checks depend on whether a site is a commerce store or a plain
 * website (SaaS marketing, blog, docs, etc.).
 *
 * Current definition (strict):
 *   - "store"   = a connected store connector (`shopify` / `woocommerce`)
 *                 OR a catalog with a search-eligible product
 *   - "website" = everything else (including WordPress sites with no
 *                 WooCommerce, Framer / Webflow marketing sites, etc.)
 *
 * The previous definition also returned "store" when scan heuristics
 * marked `install.commerce.applicable === true` or when install
 * detection returned `platform === 'shopify' | 'woocommerce'`. Those
 * are UNRELIABLE signals: a WordPress marketing site that happens to
 * mention "cart" or "store" was flagged commerce-applicable, and a
 * raw platform detection does not mean a store is actually connected.
 * The strict definition drops both and only trusts (a) a live
 * connector handshake and (b) a search-eligible catalog row.
 */

import type { InstallCoverage } from '@sill/types/api';
import type { ReadinessPlatformKind, VisibilityReport } from '@sill/types';

export interface DerivePlatformKindInput {
  readonly install: InstallCoverage | null | undefined;
  readonly visibility: VisibilityReport | null | undefined;
}

export function derivePlatformKind(
  input: DerivePlatformKindInput,
): ReadinessPlatformKind {
  const install = input.install ?? null;
  const visibility = input.visibility ?? null;
  if (install !== null && install.commerce.connected !== null) return 'store';
  const catalog_eligible = (visibility?.fields ?? []).some(
    (f) => f.id === 'catalog_search_eligible' && f.present,
  );
  if (catalog_eligible) return 'store';
  return 'website';
}
